import { supabase } from "@/integrations/supabase/client";

/** ID/passport files are kept for this many days after a programme ends, then deleted automatically. */
export const PURGE_GRACE_DAYS = 14;

export interface SensitiveDocRow {
  lead_id: string;
  lead_title: string | null;
  client_name: string | null;
  programme_end: string | null;
  purge_due: string | null;
  on_hold: boolean;
  hold_reason: string | null;
  submissions_with_docs: number;
  files_count: number;
  vault_docs_count: number;
  eligible: boolean;
  last_purged_at: string | null;
}

export type PurgeStatus = "purged" | "on_hold" | "eligible" | "waiting" | "no_end_date";

/** Mirrors the server rule in lead_id_docs_purge_due / get_sensitive_doc_overview. */
export function purgeStatus(
  r: Pick<SensitiveDocRow, "programme_end" | "on_hold" | "submissions_with_docs" | "vault_docs_count">,
  now: Date = new Date(),
): PurgeStatus {
  if (r.submissions_with_docs === 0 && r.vault_docs_count === 0) return "purged";
  if (r.on_hold) return "on_hold";
  if (!r.programme_end) return "no_end_date";
  const due = new Date(r.programme_end).getTime() + PURGE_GRACE_DAYS * 86400000;
  return due <= now.getTime() ? "eligible" : "waiting";
}

export const fmtSAST = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString("en-ZA", { timeZone: "Africa/Johannesburg", day: "numeric", month: "short", year: "numeric" }) : "—";

export async function fetchSensitiveDocs(leadId?: string): Promise<SensitiveDocRow[]> {
  const { data, error } = await (supabase as any).rpc("get_sensitive_doc_overview", { _lead_id: leadId ?? null });
  if (error) throw error;
  return (data || []) as SensitiveDocRow[];
}

export async function purgeLeads(leadIds: string[]) {
  const { data, error } = await supabase.functions.invoke("purge-lead-id-documents", { body: { lead_ids: leadIds } });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  const failed = (data?.purged || []).filter((r: any) => !r.ok);
  if (failed.length) throw new Error(failed.map((f: any) => f.error).join("; "));
  return data;
}

export async function setHold(leadId: string, hold: boolean, reason?: string) {
  const { error } = await (supabase as any).rpc("set_lead_id_docs_hold", { _lead_id: leadId, _hold: hold, _reason: reason ?? null });
  if (error) throw error;
}
